// Anlagen beim Erstellen eines Vertrags (seit 26.10.2026, Wunsch des Nutzers):
// eigene Gruppe neben den optionalen Paragraphen. Hat eine Anlage Bitrix-Felder
// und ist eine Deal-ID gesetzt, stehen ihre Dateien darunter; je Datei lassen
// sich die Seiten in einer Vorschau waehlen. Die Logik liegt in
// src/lib/dealAnlagenAuswahl.js, geladen wird ueber api/_lib/dealAnlagen.js.
import React, { useCallback, useState } from 'react';
import { createPortal } from 'react-dom';
import axios from 'axios';
import { Eye, Paperclip, RefreshCw, X as XIcon } from 'lucide-react';
import { T } from '../../lib/designTokens';
import {
  anzeigeTitel,
  dateiGroesseText,
  dateiSchluessel,
  dateienFuerBaustein,
  seitenAusMenge,
  seitenText,
} from '../../lib/dealAnlagenAuswahl';

const kleinKnopf = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '3px 7px',
  border: `1px solid ${T.rand}`,
  borderRadius: T.rChip,
  background: T.flaeche,
  color: T.navy,
  fontSize: T.tLabel,
  fontWeight: 700,
  cursor: 'pointer',
};

function Seitenwahl({ vorschau, onUebernehmen, onSchliessen }) {
  const gesamt = vorschau.daten?.seitenzahl || vorschau.daten?.seiten?.length || 0;
  const [gewaehlt, setGewaehlt] = useState(() => new Set(
    Array.isArray(vorschau.seiten) ? vorschau.seiten : Array.from({ length: gesamt }, (_, i) => i + 1),
  ));
  const istBild = vorschau.daten && vorschau.daten.art !== 'pdf';
  const umschalten = (nummer) => setGewaehlt((alt) => {
    const neu = new Set(alt);
    if (neu.has(nummer)) neu.delete(nummer); else neu.add(nummer);
    return neu;
  });

  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Seiten wählen: ${vorschau.datei.name}`}
      onClick={onSchliessen}
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(15, 23, 42, 0.45)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
    >
      <div
        onClick={(event) => event.stopPropagation()}
        style={{ width: 'min(920px, 100%)', maxHeight: '100%', display: 'flex', flexDirection: 'column', background: T.flaeche, borderRadius: T.rKarte, boxShadow: '0 24px 60px rgba(15, 23, 42, 0.25)', overflow: 'hidden' }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', borderBottom: `1px solid ${T.randWeich}` }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: T.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{vorschau.datei.name}</div>
            <div style={{ fontSize: T.tMikro, color: T.muted }}>
              {vorschau.titel} · {vorschau.datei.feldName}
              {gesamt && !istBild ? ` · ${gewaehlt.size} von ${gesamt} Seiten gewählt` : ''}
            </div>
          </div>
          {!istBild && gesamt ? (
            <>
              <button type="button" style={kleinKnopf} onClick={() => setGewaehlt(new Set(Array.from({ length: gesamt }, (_, i) => i + 1)))}>Alle</button>
              <button type="button" style={kleinKnopf} onClick={() => setGewaehlt(new Set())}>Keine</button>
            </>
          ) : null}
          <button type="button" aria-label="Schließen" onClick={onSchliessen} style={{ ...kleinKnopf, padding: 5 }}><XIcon size={14} /></button>
        </div>

        <div style={{ flex: 1, overflow: 'auto', padding: 16, background: T.grund }}>
          {vorschau.laden ? <div style={{ color: T.muted, fontSize: T.tXs }}>Vorschau wird geladen …</div> : null}
          {vorschau.fehler ? <div style={{ color: T.gefahr, fontSize: T.tXs }}>{vorschau.fehler}</div> : null}
          {vorschau.daten?.hinweis ? <div style={{ color: T.warn, fontSize: T.tMikro, marginBottom: 10 }}>{vorschau.daten.hinweis}</div> : null}
          {vorschau.daten && !vorschau.laden ? (
            <div style={{ display: 'grid', gridTemplateColumns: istBild ? '1fr' : 'repeat(auto-fill, minmax(150px, 1fr))', gap: 12 }}>
              {(vorschau.daten.seiten || []).map((bild, index) => {
                const nummer = index + 1;
                const an = istBild || gewaehlt.has(nummer);
                return (
                  <button
                    key={nummer}
                    type="button"
                    disabled={istBild}
                    onClick={() => umschalten(nummer)}
                    aria-pressed={an}
                    style={{ position: 'relative', padding: 6, border: `2px solid ${an ? T.navy : T.randWeich}`, borderRadius: T.rFeld, background: T.flaeche, cursor: istBild ? 'default' : 'pointer', opacity: an ? 1 : 0.5 }}
                  >
                    <img src={bild} alt={`Seite ${nummer}`} style={{ display: 'block', width: '100%', height: 'auto', maxHeight: istBild ? 560 : undefined, objectFit: 'contain' }} />
                    {!istBild ? (
                      <span style={{ position: 'absolute', top: 10, left: 10, padding: '1px 6px', borderRadius: T.rChip, background: an ? T.navy : T.rand, color: '#fff', fontSize: T.tLabel, fontWeight: 700 }}>
                        {an ? '✓ ' : ''}{nummer}
                      </span>
                    ) : null}
                  </button>
                );
              })}
            </div>
          ) : null}
        </div>

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, padding: '10px 16px', borderTop: `1px solid ${T.randWeich}` }}>
          <button type="button" style={{ ...kleinKnopf, padding: '6px 12px', fontSize: T.tXs }} onClick={onSchliessen}>Abbrechen</button>
          <button
            type="button"
            disabled={!vorschau.daten || (!istBild && gewaehlt.size === 0)}
            onClick={() => onUebernehmen(istBild ? null : seitenAusMenge(gewaehlt, gesamt))}
            style={{ ...kleinKnopf, padding: '6px 12px', fontSize: T.tXs, background: T.navy, borderColor: T.navy, color: '#fff' }}
          >
            Übernehmen
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}

export default function AnlagenAuswahl({
  anlagen,
  gewaehlteIds,
  nummern,
  onUmschalten,
  dealId,
  dealDateien,
  laden,
  fehler,
  onNeuLaden,
  auswahl,
  onAuswahl,
  apiBase,
  authHeaders,
}) {
  const [vorschau, setVorschau] = useState(null);
  const dealGesetzt = /^\d+$/.test(String(dealId || '').trim());

  const dateiUmschalten = useCallback((bausteinId, schluessel) => {
    const bisher = auswahl?.[bausteinId] || {};
    const neu = { ...bisher };
    if (neu[schluessel]) delete neu[schluessel]; else neu[schluessel] = { seiten: null };
    onAuswahl({ ...auswahl, [bausteinId]: neu });
  }, [auswahl, onAuswahl]);

  const oeffneVorschau = useCallback(async (baustein, datei) => {
    const schluessel = dateiSchluessel(datei.feld, datei.id);
    const start = { bausteinId: baustein.id, titel: anzeigeTitel(baustein), datei, schluessel, seiten: auswahl?.[baustein.id]?.[schluessel]?.seiten ?? null, laden: true, fehler: '', daten: null };
    setVorschau(start);
    try {
      const antwort = await axios.post(`${apiBase}/contracts/usage`, {
        action: 'deal-anlage-vorschau',
        deal_id: String(dealId).trim(),
        feld: datei.feld,
        datei_id: String(datei.id),
      }, { headers: authHeaders, timeout: 180000 });
      setVorschau((alt) => (alt && alt.schluessel === schluessel ? { ...alt, laden: false, daten: antwort.data } : alt));
    } catch (error) {
      const text = error?.response?.data?.detail || error?.message || 'Vorschau ließ sich nicht laden.';
      setVorschau((alt) => (alt && alt.schluessel === schluessel ? { ...alt, laden: false, fehler: text } : alt));
    }
  }, [apiBase, auswahl, authHeaders, dealId]);

  const seitenUebernehmen = useCallback((seiten) => {
    if (!vorschau) return;
    const bisher = auswahl?.[vorschau.bausteinId] || {};
    onAuswahl({ ...auswahl, [vorschau.bausteinId]: { ...bisher, [vorschau.schluessel]: { seiten } } });
    setVorschau(null);
  }, [auswahl, onAuswahl, vorschau]);

  if (!anlagen.length) return null;

  return (
    <div style={{ padding: '10px 16px', borderBottom: `1px solid ${T.randWeich}`, background: '#F8FAFC' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
        <Paperclip size={13} color="#475569" />
        <span style={{ flex: 1, color: '#475569', fontSize: 11, fontWeight: 700 }}>Anlagen</span>
        {dealGesetzt ? (
          <button type="button" onClick={onNeuLaden} disabled={laden} title="Dateien erneut aus Bitrix laden" style={{ ...kleinKnopf, opacity: laden ? 0.6 : 1 }}>
            <RefreshCw size={11} /> {laden ? 'Lädt …' : 'Aus Bitrix'}
          </button>
        ) : null}
      </div>
      {!dealGesetzt ? (
        <div style={{ marginBottom: 8, color: T.muted, fontSize: T.tMikro, lineHeight: 1.4 }}>
          Mit Deal-ID werden Lageplan, Grundbuchauszug und Einspeisezusage aus Bitrix gezogen.
        </div>
      ) : null}
      {fehler ? <div style={{ marginBottom: 8, color: T.gefahr, fontSize: T.tMikro }}>{fehler}</div> : null}

      {anlagen.map((baustein) => {
        const an = gewaehlteIds.includes(baustein.id);
        const hatFelder = Array.isArray(baustein.bitrix_felder) && baustein.bitrix_felder.length > 0;
        const dateien = dealGesetzt && dealDateien ? dateienFuerBaustein(baustein, dealDateien) : [];
        const gewaehlteDateien = auswahl?.[baustein.id] || {};
        return (
          <div key={baustein.id} style={{ marginBottom: 6 }}>
            <label style={{ display: 'flex', alignItems: 'flex-start', gap: 8, cursor: 'pointer' }}>
              <input type="checkbox" checked={an} onChange={() => onUmschalten(baustein.id)} style={{ marginTop: 2, flexShrink: 0 }} />
              <span style={{ fontSize: 12, fontWeight: 700, color: '#0F172A' }}>
                {an && nummern[baustein.id] ? <span style={{ color: T.navy }}>Anlage {nummern[baustein.id]} · </span> : null}
                {anzeigeTitel(baustein)}
              </span>
            </label>
            {an && dealGesetzt && hatFelder ? (
              <div style={{ margin: '4px 0 0 22px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                {dateien.length === 0 && !laden ? (
                  <div style={{ color: T.warn, fontSize: T.tMikro }}>Keine Datei im Deal – unter „PDF-Anlagen“ hochladen oder Anlage abwählen.</div>
                ) : null}
                {dateien.map((datei) => {
                  const schluessel = dateiSchluessel(datei.feld, datei.id);
                  const wahl = gewaehlteDateien[schluessel];
                  return (
                    <div key={schluessel} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '4px 6px', border: `1px solid ${T.randWeich}`, borderRadius: T.rFeld, background: T.flaeche }}>
                      <input type="checkbox" checked={Boolean(wahl)} onChange={() => dateiUmschalten(baustein.id, schluessel)} aria-label={`${datei.name} anhängen`} />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div title={datei.name} style={{ fontSize: T.tXs, color: T.ink, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{datei.name}</div>
                        <div style={{ fontSize: T.tLabel, color: T.muted }}>
                          {datei.feldName}{dateiGroesseText(datei.groesse) ? ` · ${dateiGroesseText(datei.groesse)}` : ''}
                          {wahl && datei.art === 'pdf' ? ` · ${seitenText(wahl.seiten)}` : ''}
                        </div>
                      </div>
                      <button type="button" onClick={() => oeffneVorschau(baustein, datei)} title="Datei ansehen und Seiten wählen" style={kleinKnopf}>
                        <Eye size={11} /> {datei.art === 'pdf' ? 'Seiten' : 'Ansehen'}
                      </button>
                    </div>
                  );
                })}
              </div>
            ) : null}
            {an && !hatFelder ? (
              <div style={{ margin: '2px 0 0 22px', color: T.muted, fontSize: T.tLabel }}>Datei bei Bedarf unter „PDF-Anlagen“ hochladen.</div>
            ) : null}
          </div>
        );
      })}

      {vorschau ? (
        <Seitenwahl
          key={vorschau.schluessel + (vorschau.daten ? ':geladen' : '')}
          vorschau={vorschau}
          onUebernehmen={seitenUebernehmen}
          onSchliessen={() => setVorschau(null)}
        />
      ) : null}
    </div>
  );
}
